import React, { useState, useRef, useEffect } from 'react'
import { PencilIcon } from '@heroicons/react/24/outline'

interface EditableFieldProps {
  value: string
  onSave: (value: string) => Promise<void>
  label?: string
  type?: 'text' | 'textarea'
  placeholder?: string
  disabled?: boolean
  className?: string
  labelClassName?: string
  rows?: number
}

const EditableField: React.FC<EditableFieldProps> = ({
  value,
  onSave,
  label,
  type = 'text',
  placeholder = '',
  disabled = false,
  className = '',
  labelClassName = '',
  rows = 3
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

  useEffect(() => {
    setEditValue(value)
  }, [value])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      // 對於 input 元素，選中所有文字
      if (type === 'text' && inputRef.current instanceof HTMLInputElement) {
        inputRef.current.select()
      }
    }
  }, [isEditing, type])

  const handleSave = async () => {
    // 移除 trim，保持與原值一致的比較
    if (editValue === value) {
      setIsEditing(false)
      return
    }

    setIsLoading(true)
    setError('')

    try {
      await onSave(editValue)
      setIsEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : '儲存失敗')
      setEditValue(value) // 恢復原值
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    setEditValue(value)
    setIsEditing(false)
    setError('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && type === 'text') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  // 早期返回：如果禁用則顯示唯讀視圖
  if (disabled) {
    return (
      <div>
        {label && (
          <label className={`block text-sm font-medium text-gray-700 mb-1 ${labelClassName}`}>
            {label}
          </label>
        )}
        <div className={`px-3 py-2 bg-gray-50 rounded-lg text-gray-600 ${className}`}>
          {value || <span className="text-gray-400">{placeholder}</span>}
        </div>
      </div>
    )
  }

  return (
    <div>
      {label && (
        <label className={`block text-sm font-medium text-gray-700 mb-1 ${labelClassName}`}>
          {label}
        </label>
      )}
      
      {isEditing ? (
        <div className="space-y-2">
          <div className="flex items-start space-x-2">
            {type === 'text' ? (
              <input
                ref={inputRef as React.RefObject<HTMLInputElement>}
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleSave}
                placeholder={placeholder}
                className={`input-field ${className}`}
                disabled={isLoading}
              />
            ) : (
              <textarea
                ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleSave}
                placeholder={placeholder}
                rows={rows}
                className={`input-field ${className}`}
                disabled={isLoading}
              />
            )}
          </div>
          
          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </div>
      ) : (
        <div
          onClick={() => setIsEditing(true)}
          className={`group relative cursor-pointer hover:bg-gray-50 px-3 py-2 rounded-lg transition-colors border border-gray-200 hover:border-gray-300 ${className}`}
          style={{ minHeight: type === 'textarea' ? '80px' : '42px' }}
        >
          <div className="flex items-start justify-between">
            <span className={`${value ? 'text-gray-900' : 'text-gray-400'} ${type === 'textarea' ? 'whitespace-pre-wrap' : ''} flex-1`}>
              {value || placeholder}
            </span>
            <PencilIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity ml-2 flex-shrink-0 mt-0.5" />
          </div>
        </div>
      )}
    </div>
  )
}

export default EditableField